import type { PageMeta, PageResult } from '@beonit/adaptive-raindrop';
import { apiUrl } from '../config/index.js';
import { ApiError, ApiErrorCode } from './api-error.js';
import { type UploadOptions, uploadToApi } from './upload.js';

interface Envelope<T> {
  traceId: string;
  data: T;
  meta?: Record<string, unknown>;
}

interface ErrorEnvelope {
  traceId?: string;
  error?: { code?: string; message?: string; details?: unknown };
}

type SessionExpiredHandler = () => void;

let onSessionExpired: SessionExpiredHandler = () => {};

/** Installed by `main.ts`: warning and going to the login is the app's call, not the HTTP client's. */
export function setSessionExpiredHandler(handler: SessionExpiredHandler): void {
  onSessionExpired = handler;
}

/**
 * A single refresh in flight.
 *
 * If three requests get a 401 at once, refreshing three times makes no sense:
 * the first one rotates the token and the other two would arrive with one that
 * is already spent, which the backend reads as reuse and tears the whole
 * session down.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(apiUrl('/session/refresh'), {
        method: 'POST',
        credentials: 'include',
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      // Released on the next tick so that everyone waiting reads the same result.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

async function parseError(response: Response): Promise<ApiError> {
  let body: ErrorEnvelope = {};

  try {
    body = (await response.json()) as ErrorEnvelope;
  } catch {
    // A response with no JSON (an nginx 502, say) must not break in here.
  }

  return new ApiError(
    body.error?.code ?? ApiErrorCode.INTERNAL_ERROR,
    body.error?.message ?? `Error ${response.status}`,
    response.status,
    body.traceId,
  );
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Internal use: keeps a refresh from looping into endless retries. */
  retryOnUnauthorized?: boolean;
  /**
   * Warn that the session has expired when refreshing does not help.
   *
   * The initial session probe turns this off: someone opening the application
   * without ever having signed in has lost no session, and telling them it
   * "expired" is telling them about something that never happened.
   */
  notifyOnSessionExpired?: boolean;
}

/**
 * The only place in the frontend that talks to the network.
 *
 * It does three things and only three: it sends the cookies, unwraps the
 * `{traceId, data}` envelope and, on a 401, tries to refresh **once** before
 * giving the session up for lost. Everything else is gateways built on top.
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return (await requestEnvelope<T>(path, options)).data;
}

/**
 * The same request, envelope and all.
 *
 * Only paged lists need it: `meta` is where the counts travel, and `request`
 * throws it away because nothing else has ever wanted it.
 */
async function requestEnvelope<T>(path: string, options: RequestOptions = {}): Promise<Envelope<T>> {
  const { method = 'GET', body, retryOnUnauthorized = true, notifyOnSessionExpired = true } = options;

  let response: Response;

  try {
    response = await fetch(apiUrl(path), {
      method,
      credentials: 'include',
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(ApiErrorCode.NETWORK_ERROR, 'No connection to the server', 0);
  }

  if (response.status === 401 && retryOnUnauthorized) {
    if (await refreshSession()) {
      return requestEnvelope<T>(path, { ...options, retryOnUnauthorized: false });
    }

    if (notifyOnSessionExpired) onSessionExpired();
    throw await parseError(response);
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  if (response.status === 204) {
    return { traceId: 'no-content', data: undefined as T };
  }

  return (await response.json()) as Envelope<T>;
}

/**
 * Reads a paged response as the design system's table expects one.
 *
 * `PageResult` and `PageMeta` are the design system's own types, not copies: the
 * table is the consumer, so a shape that satisfies it is the definition of correct,
 * and a second declaration here would be a second thing to keep in step.
 *
 * The backend sends the array in `data` and the counts in `meta`. A response with
 * no `meta` — every endpoint written before this one — is read as a single page of
 * everything that came, which is exactly what it is.
 */
async function requestPage<T>(path: string): Promise<PageResult<T>> {
  const envelope = await requestEnvelope<T[]>(path);
  const items = envelope.data ?? [];
  const meta = envelope.meta as Partial<PageMeta> | undefined;

  return {
    items,
    meta: {
      total: meta?.total ?? items.length,
      page: meta?.page ?? 1,
      pageSize: meta?.pageSize ?? items.length,
      totalPages: meta?.totalPages ?? 1,
    },
  };
}

export const httpClient = {
  get: <T>(path: string): Promise<T> => request<T>(path),
  /** GET that never warns about an expired session: for probes, not for actions. */
  probe: <T>(path: string): Promise<T> => request<T>(path, { notifyOnSessionExpired: false }),
  /**
   * GET of a paged list: the rows, and the counts beside them.
   *
   * Separate from `get` rather than making every caller unwrap: a screen that
   * forgets `meta` exists shows a pager stuck on page one, and nothing warns it —
   * the compiler cannot object to a field that was never asked for.
   */
  getPage: <T>(path: string): Promise<PageResult<T>> => requestPage<T>(path),
  /**
   * POST of a multipart body, with progress and cancellation.
   *
   * Here and not in a gateway, because one place in this frontend talks to the
   * network. It is implemented with `XMLHttpRequest` in `upload.ts` for the one
   * reason that matters: `fetch` cannot report upload progress, and there is no
   * option that makes it.
   *
   * For a video the destination is the storage instead — `uploadToStorage`, with a
   * pre-signed URL, so the byte never passes through the back-office.
   */
  upload: <T>(path: string, body: FormData, options?: UploadOptions): Promise<T> =>
    uploadToApi<T>(path, body, options),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),
};
